/**
 * 进度带 —— 等待态整页**唯一**的强调块(W2,2026-08-02)。
 *
 * ═══ 三条口径纪律 ═════════════════════════════════════════════════════════════
 * ① **分段而不是连续百分比**。一张图是一个不可分的单位;连续条会暗示「这张画了 60%」——
 *    后端根本不知道这件事。段数 = 张数,一段一张。
 * ② **只说「已经等了多久」,不说「还要多久」**。`estimatedSecondsRemaining` 2026-07-16
 *    已从 DTO 删除(后端从建这个字段起就写死 null),任何「预计还需 N 秒」都是编的。
 * ③ 已等时长**算不出来就不显示**:`submittedAt` 若缺时区标记会被当本地时间解析,
 *    差出一整个时区(显示「已等 8 小时」)。宁可不显示,也不显示一个错的数。
 */
import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import type { GenSlot } from "../../../model/generationProgress";

/** 已等多久(秒);算不出来 / 明显不合理时返回 null。 */
export function elapsedSeconds(submittedAt: string | null | undefined, now: number): number | null {
  if (!submittedAt) return null;
  /**
   * ★不带时区的串按**本地时间**解析,不能补 Z 当 UTC(2026-08-02 真栈跑抓到)。
   *
   * 真后端给的是 `2026-08-02T11:17:26.839` —— 没有时区标记,而 backend 的 jackson
   * `time-zone: Asia/Shanghai`,也就是说这串**就是本地时间**。第一版我补了个 Z 当 UTC 解,
   * 于是 11:17 本地被当成 11:17 UTC(= 19:17 本地),算出来是负八小时,
   * 被下面的合理性闸拦掉 → 「已等 N 秒」在真环境里**一次都不会显示**。
   * 全 mock 的 e2e 发现不了:我造的数据都是规规矩矩带 Z 的。
   *
   * ES2015+ 规定:带时间部分而无偏移量的 ISO 串按本地时区解析 —— 正是我们要的。
   */
  const t = Date.parse(submittedAt);
  if (!Number.isFinite(t)) return null;
  const sec = Math.floor((now - t) / 1000);
  // 负数(时钟不同步)或超过 6 小时(任务最长 5 分钟就被 reaper 判死)一律判为算错了
  if (sec < 0 || sec > 6 * 3600) return null;
  return sec;
}

export function waitedText(sec: number): string {
  if (sec < 60) return `已等 ${sec} 秒`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s === 0 ? `已等 ${m} 分` : `已等 ${m} 分 ${s} 秒`;
}

/** 每秒走一格的时钟(只在出图途中挂,终态不占定时器)。 */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

export function ProgressBand({
  slots,
  done,
  failed,
  total,
  submittedAt,
  className,
}: {
  slots: GenSlot[];
  done: number;
  failed: number;
  total: number;
  submittedAt?: string | null;
  className?: string;
}) {
  const now = useNow(true);
  const sec = elapsedSeconds(submittedAt, now);
  const waiting = slots.filter((s) => s.state === "waiting" && !s.deferred).length;
  const deferred = slots.filter((s) => s.deferred).length;

  return (
    <div
      className={cn("rounded-xl border border-primary/25 bg-primary/[0.04] px-4 py-3.5", className)}
    >
      <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="flex items-baseline gap-2.5">
          <span className="text-[26px] font-bold leading-none tabular-nums text-foreground">
            {done}
            <span className="text-muted-foreground">/{total}</span>
          </span>
          <span className="text-[13px] text-foreground/80">已出图</span>
          {failed > 0 && (
            <span className="text-[12.5px] font-medium text-destructive">{failed} 张没出来</span>
          )}
        </div>
        {sec != null && (
          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            {waitedText(sec)}
          </span>
        )}
      </div>

      {/* 分段进度。整条只读一个数:哪几段实了。 */}
      <div
        className="flex gap-[3px]"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        aria-label={`已出图 ${done} / ${total}`}
      >
        {slots.map((s) => (
          <span
            key={s.seq}
            className={cn(
              "h-2 flex-1 rounded-full transition-colors duration-300",
              s.state === "landed" && "bg-primary",
              s.state === "failed" && "bg-destructive",
              s.state === "waiting" && (s.deferred ? "bg-muted/40" : "bg-muted")
            )}
          />
        ))}
      </div>

      {/* ★这句只说得出「还差几张」——不指名哪一张正在画(见 generationProgress 纪律②)。
          分步出图(先出封面)要诚实:本批只出封面,后面几张**没在出**,不许说「还差 4 张正在出」。 */}
      <p className="mt-2 text-[12.5px] text-muted-foreground" aria-live="polite">
        {deferred > 0 && waiting > 0
          ? `本批先出封面这 ${waiting} 张 · 剩下 ${deferred} 张看过封面再续出`
          : waiting > 0
            ? `还差 ${waiting} 张,正在出图`
            : done > 0
              ? "全部出完了"
              : /* 一张都没出(首帧 / RUNNING 中产物短暂为空)时不许说「全部出完了」——
                   那是「0/0 · 全部出完了」矛盾话术的来源(2026-09-10)。 */
                "正在准备出图…"}
      </p>
    </div>
  );
}
